import { reportClientError } from "./reportClientError";
import { descreverErro, impressaoDoErro } from "./descreverErro";

/**
 * Handlers globais de erro do navegador → telemetria.
 *
 * Por que existe: o `reportClientError` cobria falha de envio e de abrir sessão, e o `ErrorBoundary`
 * cobre crash de render. Ficava de fora tudo que estoura FORA de um `catch` nosso — exceção em
 * handler de evento, promise rejeitada sem `.catch`, erro em callback do SDK. É justamente o que
 * costuma PRECEDER um crash: ver antes é a diferença entre corrigir e ser avisado por relato de
 * cliente (foi assim que o incidente de 2026-07-28 apareceu, ~1h20 depois).
 *
 * 🔴 **Teto obrigatório.** Uma árvore em loop de erro dispara centenas de eventos. Sem limite, a
 * telemetria vira um ataque ao nosso próprio backend e queima o orçamento de alertas (6/hora por
 * evento). Daí a deduplicação por motivo + o teto por sessão.
 *
 * 🔴 **ATRIBUIÇÃO DE ORIGEM (security review, 2026-08-28).** O `embed.js` injeta o widget DIRETO no
 * DOM do site da marca — sem iframe, sem Shadow DOM. Estes listeners são, portanto, globais da
 * PÁGINA: sem filtro eles capturam as exceções dos scripts do próprio site de apostas e das outras
 * tags de terceiros. E mensagem de erro nesse contexto carrega PII o tempo todo — um `JSON.parse`
 * que falha já embute o trecho do input, e `Promise.reject(await r.text())` rejeita com o corpo cru
 * da resposta. Isso ia até 140 chars para o log do backend e para o relay de WhatsApp da operação.
 *
 * Contrato: **só relatamos o que conseguimos atribuir ao NOSSO bundle. Na dúvida, silêncio.**
 * Perder telemetria é barato; vazar dado do apostador de um cliente não é.
 *
 * ⚠️ Consequência aceita conscientemente: script de outra origem sem `crossorigin="anonymous"`
 * chega ao `window.onerror` mascarado ("Script error.", `filename` vazio), então as exceções
 * SÍNCRONAS do próprio widget embarcado também deixam de ser relatadas. O caminho que continua
 * rendendo é o `unhandledrejection` — o `reason` é um objeto JS de verdade e o stack NÃO é
 * mascarado. Ligar `crossorigin` no `embed.js` recuperaria a outra metade, mas exige adicionar
 * CORS aos assets JS: se o cabeçalho não pegar, o navegador RECUSA o script e o widget morre por
 * inteiro. Num produto sem staging, isso é mudança para uma PR própria, com validação própria.
 *
 * Best-effort, como todo o resto da telemetria: nunca lança, nunca bloqueia o apostador.
 */

/**
 * Teto de relatos por carregamento de página. 5 é folgado para o caso real (um bug tem poucos
 * motivos distintos) e baixo o suficiente para um loop não virar enxurrada.
 */
export const MAX_RELATOS_POR_SESSAO = 5;

let instalado = false;
let relatos = 0;
let origemDoBundle: string | null = null;
const chavesVistas = new Set<string>();

/**
 * 🔴 **A chave de deduplicação NÃO é o motivo transmitido — e não pode voltar a ser.**
 *
 * Desde que o `descreverErro` parou de repassar `Error.message` (corte do texto livre da
 * telemetria), todo `Error` comum se descreve igual: `"Error: falha técnica"`. Enquanto a chave do
 * `Set` foi o motivo transmitido, dois bugs DIFERENTES colidiam: o segundo era descartado em
 * silêncio e o teto de 5 relatos por sessão virava um teto de 1. A operação via o primeiro crash da
 * carga da página e mais nada — sem erro visível, sem sinal de que algo tinha sido engolido.
 *
 * A chave é montada aqui, a partir do erro CRU, e vive só neste `Set`. O que sai para o backend
 * continua sendo o motivo redigido do `descreverErro`.
 */
const chaveDeDeduplicacao = (erro: unknown): string => impressaoDoErro(erro);

/**
 * De onde ESTE arquivo foi carregado — descoberto na carga do módulo pelo stack de um erro
 * descartável. É a única forma confiável de saber a URL do próprio bundle sem depender de detalhe
 * interno do webpack (`publicPath` no CRA é "/", que aponta para o SITE, não para o widget).
 * Em jsdom/SSR não há URL no stack → devolve `null`, e o fail-closed abaixo cala a telemetria.
 */
export const detectarOrigemDoBundle = (): string | null => {
    try {
        const linhas = (new Error().stack ?? "").split("\n");
        for (const linha of linhas) {
            const achado = linha.match(/https?:\/\/[^\s)]+?\.js/);
            if (achado) {
                return achado[0];
            }
        }
        return null;
    } catch {
        return null;
    }
};

/** Uso exclusivo dos testes — o estado é de módulo e o `resetMocks` do CRA não o zera. */
export const __resetarParaTeste = (): void => {
    instalado = false;
    relatos = 0;
    origemDoBundle = null;
    chavesVistas.clear();
};

/**
 * O erro veio do nosso bundle? `texto` é o `filename` do ErrorEvent ou o `stack` do motivo da
 * rejeição. Fail-closed: sem origem conhecida ou sem texto para comparar, a resposta é NÃO.
 */
const doNossoBundle = (texto: unknown): boolean =>
    Boolean(origemDoBundle) && typeof texto === "string" && texto.includes(origemDoBundle as string);

/**
 * Relata um erro NÃO capturado, com deduplicação, teto por sessão e impressão.
 *
 * Exportado porque o React 19 tem um segundo caminho para chegar aqui — o `onUncaughtError` do
 * `createRoot` (ver `src/index.tsx`). Os dois têm que passar pela MESMA função: dedup e teto
 * separados voltariam a deixar um loop de render virar N mensagens no WhatsApp da operação.
 */
export const relatarErroNaoCapturado = (erro: unknown): void => {
    try {
        if (relatos >= MAX_RELATOS_POR_SESSAO) {
            return;
        }
        // Mesmo bug em loop = 1 mensagem, não N. Bugs diferentes = chaves diferentes.
        const chave = chaveDeDeduplicacao(erro);
        if (chavesVistas.has(chave)) {
            return;
        }
        chavesVistas.add(chave);
        relatos += 1;
        /*
         * A impressão vai JUNTO: no backend a chave de throttle é evento + motivo, e o motivo é o
         * mesmo para todo `Error`. Sem ela, a deduplicação que acabou de funcionar aqui morre no
         * estágio seguinte e o segundo bug distinto é engolido pelo throttle.
         */
        reportClientError("uncaught_error", { reason: descreverErro(erro), errorFingerprint: chave });
    } catch {
        /* telemetria não pode derrubar o que sobrou de pé */
    }
};

export const instalarHandlersGlobais = (origem: string | null = detectarOrigemDoBundle()): void => {
    if (instalado) {
        return;
    }
    instalado = true;
    /*
     * Guardamos o DIRETÓRIO, não o arquivo. Hoje o build é um `main.js` único, mas se code-splitting
     * for ligado um dia, os chunks são irmãos — casar pelo arquivo exato mataria a telemetria em
     * silêncio. O diretório ainda exclui o site hospedeiro, que está em outra origem.
     */
    origemDoBundle = origem ? origem.replace(/[^/]*$/, "") : null;

    window.addEventListener("error", (evento: ErrorEvent) => {
        /*
         * O `filename` é a atribuição. Ele vem vazio para script de outra origem sem CORS
         * ("Script error.") e vem com a URL do SITE para os scripts dele — os dois casos caem fora.
         * Também descarta falha de carregamento de recurso (img/script), que nem exceção é.
         */
        if (!doNossoBundle(evento.filename)) {
            return;
        }
        relatarErroNaoCapturado(evento.error ?? evento.message);
    });

    window.addEventListener("unhandledrejection", (evento: Event & { reason?: unknown }) => {
        /*
         * Aqui a atribuição sai do STACK, que (ao contrário do `window.onerror`) não é mascarado
         * entre origens. Rejeição com valor primitivo ou objeto simples não tem stack → não é
         * atribuível → não sai daqui. É exatamente o caso do `Promise.reject(await r.text())`,
         * que carregaria o corpo cru da resposta do site hospedeiro.
         */
        const motivo = evento.reason as { stack?: unknown } | undefined;
        if (!doNossoBundle(motivo?.stack)) {
            return;
        }
        relatarErroNaoCapturado(evento.reason);
    });
};
